import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Trash2 } from "lucide-react";
import api from "../../lib/api";

const STATUSES = ["New Lead", "Contacted", "Interested", "Counselling", "Follow-up", "Converted", "Not Interested"];

const STATUS_COLORS = {
  "New Lead": "bg-blue-500/15 text-blue-400 border-blue-500/30",
  Contacted: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  Interested: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  Counselling: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  "Follow-up": "bg-orange-500/15 text-orange-400 border-orange-500/30",
  Converted: "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30",
  "Not Interested": "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("All");

  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Lead moved to "${status}"`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch {
      toast.error("Could not delete lead");
    }
  };

  const filtered = filter === "All" ? leads : leads.filter((l) => l.status === filter);

  const waNumber = (mobile) => {
    const digits = (mobile || "").replace(/\D/g, "");
    return digits.length === 10 ? `91${digits}` : digits;
  };

  return (
    <div data-testid="admin-leads-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-[#F9FAFB]">Leads Pipeline</h1>
          <p className="text-sm text-[#9CA3AF] mt-1">{leads.length} total enquiries · track each to conversion.</p>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="leads-filter-select"
          className="px-4 py-2.5 rounded-lg bg-[#111827] border border-[#1F2937] text-sm text-[#F9FAFB] focus:outline-none focus:border-[#10B981]"
        >
          <option value="All">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="mt-6 bg-[#111827] border border-[#1F2937] rounded-xl overflow-hidden">
        {filtered.length === 0 ? (
          <p className="px-5 py-10 text-sm text-[#6B7280]" data-testid="leads-empty">No leads in this view yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280] border-b border-[#1F2937]">
                  <th className="px-5 py-3">Lead</th>
                  <th className="px-5 py-3">Interest</th>
                  <th className="px-5 py-3">Source</th>
                  <th className="px-5 py-3">Prefers</th>
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-b border-[#1F2937]/50 last:border-0 hover:bg-[#0B0F19]/40" data-testid={`lead-row-${l.id.slice(0, 8)}`}>
                    <td className="px-5 py-3.5">
                      <p className="font-semibold text-[#F9FAFB]">{l.name}</p>
                      <p className="text-xs text-[#6B7280]">{l.mobile}{l.email ? ` · ${l.email}` : ""}</p>
                    </td>
                    <td className="px-5 py-3.5 text-[#9CA3AF] max-w-[180px] truncate">{l.interest || "—"}</td>
                    <td className="px-5 py-3.5 text-[#9CA3AF] text-xs">{l.source}</td>
                    <td className="px-5 py-3.5 text-[#9CA3AF] text-xs">{l.preferred_contact || "—"}</td>
                    <td className="px-5 py-3.5 text-[#9CA3AF] text-xs whitespace-nowrap">
                      {l.created_at ? new Date(l.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—"}
                    </td>
                    <td className="px-5 py-3.5">
                      <select
                        value={l.status}
                        onChange={(e) => updateStatus(l.id, e.target.value)}
                        data-testid={`lead-status-${l.id.slice(0, 8)}`}
                        className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold border bg-transparent focus:outline-none cursor-pointer ${STATUS_COLORS[l.status] || "bg-slate-500/15 text-slate-400 border-slate-500/30"}`}
                      >
                        {STATUSES.map((s) => <option key={s} value={s} className="bg-[#111827] text-[#F9FAFB]">{s}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={`https://wa.me/${waNumber(l.mobile)}?text=${encodeURIComponent(`Hi ${l.name}, this is SterlingNest regarding your enquiry${l.interest ? ` about ${l.interest}` : ""}.`)}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`lead-whatsapp-${l.id.slice(0, 8)}`}
                          className="p-2 rounded-lg bg-[#25D366]/15 text-[#25D366] hover:bg-[#25D366]/25 transition-colors"
                          title="WhatsApp this lead"
                        >
                          <MessageCircle size={15} />
                        </a>
                        <button
                          onClick={() => remove(l.id)}
                          data-testid={`lead-delete-${l.id.slice(0, 8)}`}
                          className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                          title="Delete lead"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
